import React from 'react';
import { Container, Body5 } from '@ui';
import DatePicker from 'react-date-picker';
import styled from 'styled-components';
import { NodeParameterRemoveButton } from '@flow/buttons/NodeParameterRemoveButton';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { useGptModels } from '@hooks/features/useGptModels';
import { generateBoolLabels, generateOnOffLabels } from '@hooks/application/useAdvancedSettings';
import {
  AgentFlowParameterWrapper,
  AgentFlowParameterLabelWrapper,
  AgentFlowParameterInlineButtonWrapper,
} from '@flow/styles';
import { createTimeInstance, formatLocalizedDateTime } from '@utils/time';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { AI_TOOLS } from '@constants';
import { getToolAdvancedSettingsFields } from '@utils/advancedSettings';

const CalendarIconWrapper = styled.div`
  svg {
    height: 17px;
    width: 17px;

    fill: ${({ theme }) => theme.themeColors.text};
  }
  padding-bottom: 1px;
`;

function OpenCalenderIcon() {
  return (
    <CalendarIconWrapper>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
      </svg>
    </CalendarIconWrapper>
  );
}

function ClearIcon() {
  return null;
}

const AdvSettingTextArea = styled.textarea`
  min-height: 32px !important;
  resize: vertical;
`;
const SelectedDocumentsButtonWrapper = styled.div`
  button {
    padding: 8px 8px 8px 0;
  }
`;
const BoolDropdownWrapper = styled.div`
  width: 120px;
`;
const DatePickerWrapper = styled.div`
  height: 32px;
  padding: 0;
  width: 100%;

  .react-date-picker__inputGroup__input {
    height: 32px !important;
  }
  .react-date-picker__inputGroup {
    padding: 0;
  }
  .react-date-picker__inputGroup__input {
    border: none;
    padding: 0;
  }

  .react-date-picker__inputGroup__input {
    &.react-date-picker__inputGroup__year {
      min-width: 56px;
    }

    min-width: 40px;
    text-align: center;
  }

  .react-date-picker__wrapper {
    border: none;
    border-radius: 4px;
    padding: 0;
  }

  .react-calendar {
    background: ${({ theme }) => theme.themeColors.appBackground};

    color: ${({ theme }) => theme.themeColors.text};

    .react-calendar__tile--now {
      border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
      background: ${({ theme }) => theme.themeColors.appBackground};
      color: ${({ theme }) => theme.themeColors.buttonText};
    }

    button {
      background: ${({ theme }) => theme.themeColors.appBackground} !important;
      color: ${({ theme }) => theme.themeColors.text} !important;

      &:disabled {
        cursor: not-allowed;
      }

      &:enabled:hover {
        border-radius: 2px;
        background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
        color: ${({ theme }) => theme.themeColors.buttonText} !important;

        * {
          color: ${({ theme }) => theme.themeColors.buttonText} !important;
        }
      }
    }
  }

  .react-calendar__navigation button:disabled {
    background: ${({ theme }) => theme.themeColors.appBackground};
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar__tile--active {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};
    color: ${({ theme }) => theme.themeColors.buttonText};
  }

  .react-calendar__tile:disabled {
    background: ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
  }
`;

function parseAdvancedSettingsFromData(data) {
  if (!data) {
    return [];
  }
  const ignoreKeys = ['user_mode'];
  const keys = Object.keys(data);
  return keys.filter((k) => !ignoreKeys.includes(k));
}

function convertAdvancedSettingKeyNameToLabel(keyName) {
  return keyName
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function filterForAvailableSettings(currentSettings) {
  const validSettings = getToolAdvancedSettingsFields(AI_TOOLS.AGENT_FLOW);
  if (!currentSettings) {
    return validSettings;
  }
  const currentSettingsKeys = Object.keys(currentSettings);
  return validSettings.filter((setting) => !currentSettingsKeys.includes(setting));
}

const AdvancedSetting = ({ label, value, onAdvancedSettingChange, onRemove, openDocSelectionModal, nodeId }) => {
  const handleAdvancedSettingChange = (value) => {
    onAdvancedSettingChange(label, value);
  };
  const handleRemoveClick = () => {
    onRemove(label);
  };
  const labelDisplay = convertAdvancedSettingKeyNameToLabel(label);
  return (
    <li>
      <Container fullWidth row verticallyCenter>
        <AgentFlowParameterLabelWrapper IsLongLabel>
          <Body5>{labelDisplay}:</Body5>
        </AgentFlowParameterLabelWrapper>
        <AdvancedSettingInputSelector
          nodeId={nodeId}
          label={label}
          value={value}
          onChange={handleAdvancedSettingChange}
          openDocSelectionModal={openDocSelectionModal}
        />
        <NodeParameterRemoveButton onClick={handleRemoveClick} />
      </Container>
    </li>
  );
};

function formatStringToDate(string) {
  return createTimeInstance(string).toDate();
}

function formatDateToString(date) {
  return formatLocalizedDateTime('api', createTimeInstance(date));
}
const DateSelectionInput = ({ label, value, onChange }) => {
  const [defaultDate] = React.useState(formatDateToString(createTimeInstance().toDate()));
  const [todayDate] = React.useState(createTimeInstance().toDate());

  const handleDateChange = (date) => {
    const nextValue = formatDateToString(date);
    onChange(nextValue);
  };

  React.useEffect(() => {
    if (!value) {
      handleDateChange(defaultDate);
    }
  }, []);

  return (
    <DatePickerWrapper>
      {value ? (
        <DatePicker
          name={label}
          calendarIcon={OpenCalenderIcon}
          clearIcon={ClearIcon}
          onChange={handleDateChange}
          value={formatStringToDate(value)}
          maxDate={todayDate}
        />
      ) : null}
    </DatePickerWrapper>
  );
};

function parseStringList(value) {
  if (!value) {
    return [];
  }
  return value.split(',');
}
function formatStringList(value) {
  if (!value || !Array.isArray(value)) {
    return '';
  }
  return value.join(',');
}
const StringListInput = ({ value, onChange }) => {
  const handleChange = (e) => {
    const value = e.target.value;
    const parsedValue = parseStringList(value);
    onChange(parsedValue);
  };
  return <input value={formatStringList(value)} onChange={handleChange} />;
};

const AdvancedSettingInputSelector = ({ label, value, onChange, openDocSelectionModal }) => {
  const [AdvancedSettingsBoolSettings] = React.useState(generateBoolLabels());
  const [AdvancedSettingsOnOffSettings] = React.useState(generateOnOffLabels());
  const { models } = useGptModels();

  if (label === 'doc_list' || label === 'overrides') {
    return <StringListInput value={value} onChange={onChange} />;
  }
  if (['start_date', 'end_date'].includes(label)) {
    return <DateSelectionInput label={label} value={value} onChange={(date) => onChange(date)} />;
  }
  // if
  if (label === 'model') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value={null}>Select One...</option>
        {models.map((model) => (
          <option key={model.key} value={model.id}>
            {model.display_name}
          </option>
        ))}
      </select>
    );
  }
  if (AdvancedSettingsBoolSettings.includes(label)) {
    const handleBoolChange = () => {
      onChange(!value);
    };
    return (
      <>
        <Container row fullWidth verticallyCenter style={{ height: '32px' }} top={2}>
          <input
            type="checkbox"
            id={'checkbox-' + label}
            checked={value}
            onClick={handleBoolChange}
            style={{ cursor: 'pointer', marginTop: '0px', marginLeft: '0px', width: '18px', height: '18px' }}
          />
        </Container>
      </>
    );
  }
  if (AdvancedSettingsOnOffSettings.includes(label)) {
    const handleOnOffChange = (e) => {
      onChange(e.target.value);
    };
    return (
      <>
        <Container row fullWidth verticallyCenter style={{ height: '32px' }} top={2}>
          <BoolDropdownWrapper>
            <select value={value} onChange={handleOnOffChange}>
              <option value="on">On</option>
              <option value="off">Off</option>
              <option value="auto">Auto</option>
              <option value="">Omitted</option>
            </select>
          </BoolDropdownWrapper>
        </Container>
      </>
    );
  }
  if (!isNaN(value)) {
    return <AdvSettingTextArea value={value} onChange={(e) => onChange(e.target.value)} style={{ height: '20px' }} />;
  }
  return <input value={value} onChange={(e) => onChange(e.target.value)} />;
};

const AddAdvancedSettingSelector = ({ onAddSetting, currentSettings }) => {
  const [availableSettings, setAvailableSettings] = React.useState(filterForAvailableSettings(currentSettings));
  const [selectedSetting, setSelectedSetting] = React.useState(null);

  React.useEffect(() => {
    setAvailableSettings(filterForAvailableSettings(currentSettings));
  }, [currentSettings]);

  const handleSettingChange = (e) => {
    setSelectedSetting(e.target.value);
  };

  const handleAddSettingClick = () => {
    if (!selectedSetting) return;

    onAddSetting(selectedSetting);
    setSelectedSetting(null);
  };

  return (
    <Container row verticallyCenter top={8} bottom={4}>
      <select value={selectedSetting} onChange={handleSettingChange}>
        <option value={null}>Select One...</option>
        {availableSettings.sort().map((setting) => (
          <option key={setting} value={setting}>
            {convertAdvancedSettingKeyNameToLabel(setting)}
          </option>
        ))}
      </select>

      <AgentFlowParameterInlineButtonWrapper>
        <FlatButton onClick={handleAddSettingClick}>Add Setting</FlatButton>
      </AgentFlowParameterInlineButtonWrapper>
    </Container>
  );
};

export const AdvancedSettingsParameter = ({ parentNodeId, data, value, onRemove, onChange, openDocSelectionModal }) => {
  const { keyName, label } = data;
  const [advancedSettings, setAdvancedSettings] = React.useState([]);
  const [isAddingSetting, setIsAddingSetting] = React.useState(false);

  React.useEffect(() => {
    setAdvancedSettings(parseAdvancedSettingsFromData(value));
  }, [value]);

  const handleSettingChange = (changeKeyName, changeValue) => {
    const clonedValue = { ...value };
    clonedValue[changeKeyName] = changeValue;
    onChange(keyName, clonedValue);
  };

  const handleRemoveSetting = (label) => {
    const clonedValue = { ...value };
    delete clonedValue[label];
    onChange(keyName, clonedValue);
  };

  const handleAddItem = () => {
    setIsAddingSetting(true);
  };

  const onAddSetting = (setting) => {
    if (generateBoolLabels().includes(setting)) {
      handleSettingChange(setting, true);
    } else if (generateOnOffLabels().includes(setting)) {
      handleSettingChange(setting, 'on');
    } else {
      handleSettingChange(setting, '');
    }
    setIsAddingSetting(false);
  };

  const handleRemoveClick = () => onRemove(keyName);

  return (
    <AgentFlowParameterWrapper>
      <Container fullWidth>
        <Container row verticallyCenter spaceBetween fullWidth>
          <Container row verticallyCenter>
            <AgentFlowParameterLabelWrapper IsLongLabel={label === 'include_full_responses_as_inputs'}>
              <Body5>{label}:</Body5>
            </AgentFlowParameterLabelWrapper>
            {!isAddingSetting && (
              <div className="read-only-hide">
                <TransparentFlatButton onClick={handleAddItem}>Add Setting</TransparentFlatButton>
              </div>
            )}
          </Container>
          <NodeParameterRemoveButton onClick={handleRemoveClick} />
        </Container>
        {isAddingSetting && (
          <div className="read-only-hide">
            <AddAdvancedSettingSelector onAddSetting={onAddSetting} currentSettings={value} />
          </div>
        )}
        <ul>
          {advancedSettings.map((keyName) => (
            <AdvancedSetting
              nodeId={parentNodeId}
              key={keyName}
              label={keyName}
              value={value[keyName]}
              onAdvancedSettingChange={handleSettingChange}
              onRemove={handleRemoveSetting}
              openDocSelectionModal={openDocSelectionModal}
            />
          ))}
        </ul>
      </Container>
    </AgentFlowParameterWrapper>
  );
};
